import React, { useMemo, useState } from 'react';
import { LogIn, LogOut, X, Loader2, Car, Footprints, Bike, Smartphone } from 'lucide-react';
import { cn } from '../../utils/cn';
import { LicensePlate } from '../ui/LicensePlate';
import {
  MANUAL_ACCESS_MODES,
  ManualAccessModeId,
  ManualAccessSelection,
  manualAccessMode,
  manualAccessIsComplete,
  manualAccessDescription,
} from '../../utils/manualAccessMode';

const MODE_ICON: Record<ManualAccessModeId, React.ComponentType<{ className?: string }>> = {
  a_pe: Footprints,
  veiculo: Car,
  aplicativo: Smartphone,
  bicicleta: Bike,
};

export interface ManualAccessVehicle {
  id: string;
  plate: string;
  brand?: string;
  model?: string;
  is_active?: boolean;
}

/**
 * HOW the person came in or went out, asked at the moment the porteiro registers it.
 *
 * The manual entrada used to be a yes/no confirmation, so the event it wrote said "entrada" and
 * nothing else — the one thing nobody had to be told. What the record was missing is the part only
 * the person at the desk knows: on foot, in their own car, in an Uber whose plate they wrote down.
 *
 * The person's own vehicles are offered as buttons rather than typed, because a plate typed at a
 * desk with somebody waiting is where the typos come from, and a plate that does not match the
 * registered one is worse than no plate at all.
 */
export const ManualAccessDialog: React.FC<{
  open: boolean;
  direction: 'entrada' | 'saida';
  personName: string;
  vehicles?: ManualAccessVehicle[];
  isProcessing?: boolean;
  darkMode?: boolean;
  onCancel: () => void;
  onConfirm: (selection: ManualAccessSelection) => void;
}> = ({ open, direction, personName, vehicles = [], isProcessing = false, darkMode = false, onCancel, onConfirm }) => {
  const [mode, setMode] = useState<ManualAccessModeId>('a_pe');
  const [plate, setPlate] = useState('');
  const [vehicleId, setVehicleId] = useState<string | null>(null);
  // Driving is the default for one's own car — a resident arriving in it is at the wheel far more
  // often than not, and the common case should need no extra click.
  const [isDriver, setIsDriver] = useState(true);

  const ownVehicles = useMemo(
    () => vehicles.filter((v) => v.is_active !== false && v.plate),
    [vehicles],
  );

  if (!open) return null;

  const current = manualAccessMode(mode);
  const selection: ManualAccessSelection = { mode, plate, vehicleId, isDriver };
  const complete = manualAccessIsComplete(selection);
  const isEntrada = direction === 'entrada';

  const pick = (next: ManualAccessModeId) => {
    setMode(next);
    // Clearing on change: a plate left over from "De veículo" must not ride along into "A pé",
    // where the form no longer shows it and nobody could notice it was still there.
    setPlate('');
    setVehicleId(null);
    setIsDriver(true);
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4" onClick={onCancel}>
      <div
        className={cn('w-full max-w-2xl rounded-2xl border shadow-2xl overflow-hidden',
          darkMode ? 'bg-[#111113] border-gray-800' : 'bg-white border-gray-200')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={cn('flex items-center justify-between px-6 py-4 border-b',
          darkMode ? 'border-gray-800' : 'border-gray-100')}>
          <div className="flex items-center gap-3 min-w-0">
            <div className={cn('flex-shrink-0 h-10 w-10 rounded-xl flex items-center justify-center',
              isEntrada
                ? (darkMode ? 'bg-green-500/15' : 'bg-green-50')
                : (darkMode ? 'bg-blue-500/15' : 'bg-blue-50'))}>
              {isEntrada
                ? <LogIn className={cn('h-5 w-5', darkMode ? 'text-green-400' : 'text-green-600')} />
                : <LogOut className={cn('h-5 w-5', darkMode ? 'text-blue-400' : 'text-blue-600')} />}
            </div>
            <div className="min-w-0">
              {/* The action's full name, the same words the button in the sidebar promises and the
                  same ones the operator will look for in the history. */}
              <p className={cn('text-lg font-bold leading-tight', darkMode ? 'text-white' : 'text-gray-900')}>
                {isEntrada ? 'Registrar Entrada Manual' : 'Registrar Saída Manual'}
              </p>
              <p className={cn('text-sm truncate', darkMode ? 'text-gray-500' : 'text-gray-500')}>{personName}</p>
            </div>
          </div>
          <button onClick={onCancel} className={cn('p-2 rounded-lg', darkMode ? 'text-gray-500 hover:bg-gray-800' : 'text-gray-400 hover:bg-gray-100')} aria-label="Fechar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className={cn('text-[11px] font-bold uppercase tracking-widest', darkMode ? 'text-gray-600' : 'text-gray-400')}>
            {isEntrada ? 'Como entrou' : 'Como saiu'}
          </p>

          <div className="grid grid-cols-3 gap-2.5">
            {MANUAL_ACCESS_MODES.map((m) => {
              const Icon = MODE_ICON[m.id];
              const active = mode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => pick(m.id)}
                  aria-pressed={active}
                  className={cn('flex flex-col items-center justify-center gap-2 px-3 py-4 rounded-xl border text-sm transition-colors',
                    active
                      ? (darkMode ? 'border-blue-400 bg-blue-500/10 text-blue-300 font-medium' : 'border-blue-600 bg-blue-50 text-blue-700 font-semibold')
                      : (darkMode ? 'border-gray-800 text-gray-300 hover:bg-gray-800/60' : 'border-gray-200 text-gray-600 hover:bg-gray-50'))}
                >
                  <Icon className="h-6 w-6 flex-shrink-0" />
                  <span className="text-center leading-tight">{m.label}</span>
                </button>
              );
            })}
          </div>

          {current.offersOwnVehicles && ownVehicles.length > 0 && (
            <div className="space-y-1.5">
              <p className={cn('text-[11px] font-bold uppercase tracking-widest', darkMode ? 'text-gray-600' : 'text-gray-400')}>
                Veículos cadastrados
              </p>
              <div className="flex flex-wrap gap-1.5">
                {ownVehicles.map((v) => {
                  const active = vehicleId === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => { setVehicleId(v.id); setPlate(v.plate); }}
                      className={cn('flex items-center gap-2 px-2 py-1.5 rounded-lg border transition-colors',
                        active
                          ? (darkMode ? 'border-blue-400 bg-blue-500/10' : 'border-blue-600 bg-blue-50')
                          : (darkMode ? 'border-gray-800 hover:bg-gray-800/60' : 'border-gray-200 hover:bg-gray-50'))}
                    >
                      <LicensePlate plate={v.plate} size="sm" />
                      {(v.brand || v.model) && (
                        <span className={cn('text-[11px]', darkMode ? 'text-gray-400' : 'text-gray-500')}>
                          {[v.brand, v.model].filter(Boolean).join(' ')}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {current.wantsPlate && (
            <div className="space-y-1">
              <label className={cn('text-[11px] font-bold uppercase tracking-widest', darkMode ? 'text-gray-600' : 'text-gray-400')}>
                Placa {current.plateRequired ? '' : '(opcional)'}
              </label>
              <input
                value={plate}
                onChange={(e) => { setPlate(e.target.value.toUpperCase()); setVehicleId(null); }}
                placeholder="ABC1D23"
                aria-label="Placa"
                className={cn('w-full px-4 py-2.5 rounded-lg border text-base font-mono tracking-wider uppercase',
                  darkMode ? 'bg-gray-900 border-gray-800 text-gray-200' : 'bg-white border-gray-200 text-gray-900')}
              />
            </div>
          )}

          {current.asksDriver && (
            <div className="space-y-1.5">
              <p className={cn('text-[11px] font-bold uppercase tracking-widest', darkMode ? 'text-gray-600' : 'text-gray-400')}>
                Quem dirigia
              </p>
              {/* The difference between "the car is inside" and "the car dropped them and left",
                  which is the question a porteiro gets asked later. */}
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  { driver: true, label: 'Motorista' },
                  { driver: false, label: 'Passageiro' },
                ].map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    onClick={() => setIsDriver(opt.driver)}
                    aria-pressed={isDriver === opt.driver}
                    className={cn('px-3 py-2.5 rounded-xl border text-sm transition-colors',
                      isDriver === opt.driver
                        ? (darkMode ? 'border-blue-400 bg-blue-500/10 text-blue-300 font-medium' : 'border-blue-600 bg-blue-50 text-blue-700 font-semibold')
                        : (darkMode ? 'border-gray-800 text-gray-300 hover:bg-gray-800/60' : 'border-gray-200 text-gray-600 hover:bg-gray-50'))}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* The exact sentence that will land in the history, before it lands there. */}
          <div className={cn('rounded-xl px-4 py-3 border',
            darkMode ? 'bg-gray-900/60 border-gray-800' : 'bg-gray-50 border-gray-200')}>
            <p className={cn('text-[11px] font-bold uppercase tracking-widest mb-1', darkMode ? 'text-gray-600' : 'text-gray-400')}>
              Ficará no histórico
            </p>
            <p className={cn('text-base font-medium', darkMode ? 'text-gray-200' : 'text-gray-800')}>
              {manualAccessDescription(direction, selection)}
            </p>
          </div>
        </div>

        <div className={cn('flex items-center justify-end gap-2.5 px-6 py-4 border-t',
          darkMode ? 'border-gray-800' : 'border-gray-100')}>
          <button
            onClick={onCancel}
            disabled={isProcessing}
            className={cn('px-4 py-2.5 rounded-lg text-sm font-medium border disabled:opacity-50',
              darkMode ? 'border-gray-800 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50')}
          >
            Cancelar
          </button>
          <button
            onClick={() => onConfirm(selection)}
            disabled={!complete || isProcessing}
            className={cn('inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50',
              isEntrada ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700')}
          >
            {isProcessing && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEntrada ? 'Registrar Entrada' : 'Registrar Saída'}
          </button>
        </div>
      </div>
    </div>
  );
};
